import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  analyzeChatRoom,
  clearChatRoomConversation,
  createChatRoom,
  createChatRoomInvite,
  deleteChatRoomMessages,
  getBoundEmail,
  getBoundChatroomUserId,
  getChatRoom,
  getChatRoomMemory,
  getChatRoomMessages,
  joinChatRoom,
  listChatRooms,
  postChatRoomMessage,
  rebuildChatRoomMemory,
  revokeChatRoomInvites,
} from "../../api";
import { todasMusicas } from "../../musicas";
import ChatRoomInvite from "./ChatRoomInvite";
import ChatRoomMemoryPanel from "./ChatRoomMemoryPanel";
import ChatRoomView from "./ChatRoomView";
import VanJudgementModal from "./VanJudgementModal";
import "./chatrooms.css";


const CHATROOM_FONT_SCALE_KEY = "vh_chatrooms_font_scale";


function deriveDefaultDisplayName() {
  const email = String(getBoundEmail() || "").trim().toLowerCase();
  if (!email.includes("@")) return "";
  const local = email.split("@", 1)[0].replace(/[._-]+/g, " ").trim();
  return local ? local.slice(0, 60) : "";
}


function readRoomIdFromUrl() {
  try {
    const url = new URL(window.location.href);
    return String(url.searchParams.get("room") || "").trim();
  } catch {
    return "";
  }
}


function readJoinTokenFromUrl() {
  try {
    const url = new URL(window.location.href);
    return String(url.searchParams.get("token") || "").trim();
  } catch {
    return "";
  }
}


function writeRoomToUrl(roomId) {
  try {
    const url = new URL(window.location.href);
    url.pathname = "/chatrooms";
    if (roomId) {
      url.searchParams.set("room", roomId);
    } else {
      url.searchParams.delete("room");
    }
    url.searchParams.delete("token");
    window.history.replaceState({}, "", `${url.pathname}${url.search}`);
  } catch {}
}


function formatEpoch(epoch) {
  const value = Number(epoch || 0);
  if (!value) return "-";
  try {
    return new Date(value * 1000).toLocaleString();
  } catch {
    return String(value);
  }
}


function trackLabel(track) {
  const raw = String(track || "");
  if (!raw) return "silencio";
  const pieces = raw.split("/");
  try {
    return decodeURIComponent(String(pieces[pieces.length - 1] || raw))
      .replace(/\.[a-z0-9]+$/i, "")
      .replace(/[_-]+/g, " ")
      .trim();
  } catch {
    return String(pieces[pieces.length - 1] || raw)
      .replace(/\.[a-z0-9]+$/i, "")
      .replace(/[_-]+/g, " ")
      .trim();
  }
}


function buildDefaultRoomTitle(displayName) {
  const baseName = String(displayName || "").trim();
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const hour = String(now.getHours()).padStart(2, "0");
  const minute = String(now.getMinutes()).padStart(2, "0");
  if (baseName) {
    return `Sala de ${baseName} ${day}/${month} ${hour}:${minute}`;
  }
  return `Sala do Tribunal ${day}/${month} ${hour}:${minute}`;
}


function isCompactViewport() {
  try {
    return window.innerWidth <= 980;
  } catch {
    return false;
  }
}


function normalizeFontScale(value) {
  const numeric = Number(value || 1);
  if (!Number.isFinite(numeric)) return 1;
  return Math.min(1.6, Math.max(0.9, numeric));
}


function readStoredFontScale() {
  try {
    return normalizeFontScale(window.localStorage.getItem(CHATROOM_FONT_SCALE_KEY));
  } catch {
    return 1;
  }
}


export default function VHChatRoomInterface({ onNavigate, disableLocalAudio = false }) {
  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(() => readRoomIdFromUrl());
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [badges, setBadges] = useState([]);
  const [memory, setMemory] = useState(null);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [memoryBusy, setMemoryBusy] = useState(false);
  const [error, setError] = useState("");
  const [createTitle, setCreateTitle] = useState("");
  const [displayName, setDisplayName] = useState(() => deriveDefaultDisplayName());
  const [fontScale, setFontScale] = useState(() => readStoredFontScale());
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [judgementOpen, setJudgementOpen] = useState(false);
  const [judgementData, setJudgementData] = useState(null);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return isCompactViewport();
  });

  const [musicaAtual, setMusicaAtual] = useState("");
  const [musicStopped, setMusicStopped] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [hasMusicHistory, setHasMusicHistory] = useState(false);

  const audioRef = useRef(null);
  const musicHistoryRef = useRef([]);
  const isSwitchingTrackRef = useRef(false);
  const voicePausedMusicRef = useRef(false);
  const musicStoppedRef = useRef(false);

  const currentUserId = String(getBoundChatroomUserId() || "").trim();
  const selectedRoom = useMemo(
    () => rooms.find((item) => String(item?.room_id || "") === String(activeRoomId || "")) || null,
    [rooms, activeRoomId]
  );

  const musicStatusText = disableLocalAudio
    ? "Audio global ativo"
    : musicStopped
    ? "Musica pausada"
    : isMusicPlaying
    ? `Tocando: ${trackLabel(musicaAtual)}`
    : "Musica pronta";
  const stopToggleLabel = musicStopped ? "Retomar musica" : "Pausar musica";

  const playRandomTrack = useCallback((options = {}) => {
    if (disableLocalAudio) return;
    const force = options?.force === true;
    const pool = Array.isArray(todasMusicas) ? todasMusicas.filter(Boolean) : [];
    if (!pool.length) return;
    if (!force && isSwitchingTrackRef.current) return;

    const audio = audioRef.current;
    if (!audio) return;
    let candidate = pool[Math.floor(Math.random() * pool.length)];
    let attempts = 0;
    while (candidate === musicaAtual && pool.length > 1 && attempts < 8) {
      candidate = pool[Math.floor(Math.random() * pool.length)];
      attempts += 1;
    }

    if (musicaAtual) {
      const history = musicHistoryRef.current;
      history.push(musicaAtual);
      if (history.length > 20) history.shift();
      setHasMusicHistory(history.length > 0);
    }

    isSwitchingTrackRef.current = true;
    try {
      audio.pause();
    } catch {}
    audio.src = candidate;
    audio.currentTime = 0;
    audio.volume = 1;
    audio
      .play()
      .then(() => {
        setMusicaAtual(candidate);
        setMusicStopped(false);
        setIsMusicPlaying(true);
      })
      .catch(() => {
        setIsMusicPlaying(false);
      })
      .finally(() => {
        isSwitchingTrackRef.current = false;
      });
  }, [disableLocalAudio, musicaAtual]);

  const refreshRooms = useCallback(async (preferredRoomId = "") => {
    setLoadingRooms(true);
    try {
      const data = await listChatRooms();
      const items = Array.isArray(data?.items) ? data.items : [];
      setRooms(items);
      const preferred = String(preferredRoomId || "").trim();
      const firstRoomId = String(items[0]?.room_id || "").trim();
      setActiveRoomId((currentRoomId) => {
        const current = String(currentRoomId || "").trim();
        return preferred || current || firstRoomId || currentRoomId;
      });
      setError("");
    } catch (fetchError) {
      setError(String(fetchError?.message || "Falha ao carregar salas."));
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  const loadRoom = useCallback(async (roomId) => {
    const rid = String(roomId || "").trim();
    if (!rid) {
      setActiveRoom(null);
      setMessages([]);
      setBadges([]);
      setMemory(null);
      return;
    }
    try {
      const [roomData, messageData] = await Promise.all([
        getChatRoom(rid),
        getChatRoomMessages(rid, { limit: 200 }),
      ]);
      setActiveRoom(roomData);
      setMessages(Array.isArray(messageData?.items) ? messageData.items : []);
      setBadges(Array.isArray(messageData?.badges) ? messageData.badges : []);
      setMemory(messageData?.memory || null);
      if (messageData?.latest_analysis?.result) {
        setJudgementData(messageData.latest_analysis);
      }
      setError("");
      writeRoomToUrl(rid);
    } catch (fetchError) {
      setError(String(fetchError?.message || "Falha ao abrir sala."));
    }
  }, []);

  useEffect(() => {
    refreshRooms(readRoomIdFromUrl());
  }, [refreshRooms]);

  useEffect(() => {
    if (!activeRoomId) return;
    loadRoom(activeRoomId);
  }, [activeRoomId, loadRoom]);

  useEffect(() => {
    const token = readJoinTokenFromUrl();
    if (!token) return;
    let cancelled = false;

    async function handleJoinByToken() {
      setBusy(true);
      try {
        const joined = await joinChatRoom({
          token,
          display_name: displayName || deriveDefaultDisplayName(),
        });
        if (cancelled) return;
        const joinedRoomId = String(joined?.room?.room_id || "").trim();
        await refreshRooms(joinedRoomId);
        setActiveRoomId(joinedRoomId);
      } catch (joinError) {
        if (!cancelled) {
          setError(String(joinError?.message || "Falha ao entrar na sala."));
        }
      } finally {
        if (!cancelled) {
          setBusy(false);
        }
      }
    }

    handleJoinByToken();
    return () => {
      cancelled = true;
    };
  }, [displayName, refreshRooms]);

  useEffect(() => {
    if (!activeRoomId) return undefined;
    const timer = window.setInterval(() => {
      loadRoom(activeRoomId);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [activeRoomId, loadRoom]);

  useEffect(() => {
    if (!activeRoomId) return;
    if (isCompactViewport()) {
      setSidebarCollapsed(true);
    }
  }, [activeRoomId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(CHATROOM_FONT_SCALE_KEY, String(fontScale));
    } catch {}
  }, [fontScale]);

  useEffect(() => {
    musicStoppedRef.current = musicStopped;
  }, [musicStopped]);

  useEffect(() => {
    if (disableLocalAudio) return undefined;
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onPlay = () => setIsMusicPlaying(true);
    const onPause = () => setIsMusicPlaying(false);
    const onEnded = () => {
      if (!musicStopped) {
        playRandomTrack({ force: true });
      }
    };
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onPause);
    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onPause);
    };
  }, [disableLocalAudio, musicStopped, playRandomTrack]);

  async function handleCreateRoom(event) {
    event.preventDefault();
    const title = String(createTitle || "").trim() || buildDefaultRoomTitle(displayName || deriveDefaultDisplayName());
    setBusy(true);
    try {
      const created = await createChatRoom({
        title,
        display_name: displayName || deriveDefaultDisplayName(),
      });
      const roomId = String(created?.room?.room_id || "").trim();
      if (!roomId) {
        throw new Error("Criacao de sala sem room_id.");
      }
      setCreateTitle("");
      setActiveRoomId(roomId);
      setActiveRoom(created);
      setBadges(Array.isArray(created?.badges) ? created.badges : []);
      setMessages([]);
      setMemory(null);
      setRooms((currentRooms) => {
        const nextItem = {
          ...(created?.room || {}),
          participant_count: Array.isArray(created?.participants) ? created.participants.length : 1,
          message_count: 0,
          badges: Array.isArray(created?.badges) ? created.badges : [],
        };
        const filtered = (Array.isArray(currentRooms) ? currentRooms : []).filter(
          (item) => String(item?.room_id || "") !== roomId
        );
        return [nextItem, ...filtered];
      });
      setSidebarCollapsed(isCompactViewport());
      writeRoomToUrl(roomId);
      await loadRoom(roomId);
      await refreshRooms(roomId);
      setError("");
    } catch (createError) {
      setError(String(createError?.message || "Falha ao criar sala."));
    } finally {
      setBusy(false);
    }
  }

  const pauseMusicForVoice = useCallback(() => {
    if (disableLocalAudio) return;
    if (musicStoppedRef.current) return;
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      voicePausedMusicRef.current = true;
      try {
        audio.pause();
      } catch {}
      setIsMusicPlaying(false);
    }
  }, [disableLocalAudio]);

  const resumeMusicAfterVoice = useCallback(() => {
    if (disableLocalAudio) return;
    if (!voicePausedMusicRef.current) return;
    voicePausedMusicRef.current = false;
    if (musicStoppedRef.current) return;
    const audio = audioRef.current;
    if (audio && audio.src) {
      audio
        .play()
        .then(() => {
          setIsMusicPlaying(true);
          setMusicStopped(false);
        })
        .catch(() => {
          setIsMusicPlaying(false);
        });
      return;
    }
    playRandomTrack({ force: true });
  }, [disableLocalAudio, playRandomTrack]);

  async function handleSend(text) {
    if (!activeRoomId) return;
    setBusy(true);
    try {
      const posted = await postChatRoomMessage(activeRoomId, {
        text,
        display_name: displayName || deriveDefaultDisplayName(),
      });
      if (!disableLocalAudio && !musicaAtual && !musicStopped) {
        playRandomTrack({ force: true });
      }
      if (posted?.mention?.analysis_result) {
        setJudgementData({
          analysis_request: posted.mention.analysis_request,
          analysis_result: posted.mention.analysis_result,
          conflict_map_text: posted.mention.conflict_map_text || "",
        });
        setJudgementOpen(true);
      }
      await loadRoom(activeRoomId);
      const latest = posted?.latest_analysis;
      if (latest?.result) {
        setJudgementData(latest);
      }
    } catch (sendError) {
      setError(String(sendError?.message || "Falha ao enviar mensagem."));
    } finally {
      setBusy(false);
    }
  }

  async function handleAnalyze(lens, requestId = "") {
    if (!activeRoomId) return;
    setBusy(true);
    try {
      const result = await analyzeChatRoom(activeRoomId, {
        lens,
        request_id: requestId,
        scope_type: "last_n",
        scope_value: 20,
      });
      if (result?.analysis_result) {
        setJudgementData(result);
        setJudgementOpen(true);
      }
      if (result?.rate_limited) {
        setError("O Tribunal so pode emitir um novo julgamento por sala a cada 60 segundos.");
      }
      await loadRoom(activeRoomId);
      await refreshRooms(activeRoomId);
    } catch (analysisError) {
      setError(String(analysisError?.message || "Falha ao analisar sala."));
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerateInvite() {
    if (!activeRoomId) return;
    try {
      const data = await createChatRoomInvite(activeRoomId, {
        expires_in_hours: 24,
        max_uses: 1,
      });
      const normalized = {
        ...data,
        expires_at: formatEpoch(data?.expires_at),
      };
      setInviteData(normalized);
      setInviteOpen(true);
      setError("");
      return normalized;
    } catch (inviteError) {
      setError(String(inviteError?.message || "Falha ao gerar convite."));
      throw inviteError;
    }
  }

  async function handleRevokeInvites() {
    if (!activeRoomId) return;
    try {
      await revokeChatRoomInvites(activeRoomId);
      setInviteData(null);
    } catch (revokeError) {
      setError(String(revokeError?.message || "Falha ao revogar convites."));
    }
  }

  async function handleOpenMemory() {
    if (!activeRoomId) return;
    setMemoryBusy(true);
    try {
      const data = await getChatRoomMemory(activeRoomId);
      setMemory(data?.memory || null);
      setMemoryOpen(true);
      setError("");
    } catch (memoryError) {
      setError(String(memoryError?.message || "Falha ao abrir memoria da sala."));
    } finally {
      setMemoryBusy(false);
    }
  }

  async function handleRebuildMemory() {
    if (!activeRoomId) return;
    setMemoryBusy(true);
    try {
      const data = await rebuildChatRoomMemory(activeRoomId);
      setMemory(data?.memory || null);
      setError("");
    } catch (memoryError) {
      setError(String(memoryError?.message || "Falha ao reconstruir memoria da sala."));
    } finally {
      setMemoryBusy(false);
    }
  }

  async function handleClearConversation() {
    if (!activeRoomId) return;
    if (!window.confirm("Apagar toda a conversa desta sala? Isso remove mensagens, vereditos e memoria derivada.")) {
      return;
    }
    setMemoryBusy(true);
    try {
      const data = await clearChatRoomConversation(activeRoomId);
      setMessages([]);
      setBadges(Array.isArray(data?.badges) ? data.badges : []);
      setMemory(data?.memory || { room: null, participants: [] });
      setJudgementData(null);
      await loadRoom(activeRoomId);
      await refreshRooms(activeRoomId);
      setError("");
    } catch (clearError) {
      setError(String(clearError?.message || "Falha ao apagar a conversa da sala."));
    } finally {
      setMemoryBusy(false);
    }
  }

  async function handleDeleteSelectedMessages(messageIds = []) {
    if (!activeRoomId) return;
    const ids = Array.isArray(messageIds) ? messageIds.map((item) => String(item || "").trim()).filter(Boolean) : [];
    if (!ids.length) return;
    if (
      !window.confirm(
        `Apagar ${ids.length} mensagem(ns) selecionada(s)? Vereditos e memoria derivada da sala serao reconstruidos.`
      )
    ) {
      return;
    }
    setMemoryBusy(true);
    try {
      const data = await deleteChatRoomMessages(activeRoomId, ids);
      setMessages(Array.isArray(data?.items) ? data.items : []);
      setBadges(Array.isArray(data?.badges) ? data.badges : []);
      setMemory(data?.memory || { room: null, participants: [] });
      setJudgementData(null);
      await loadRoom(activeRoomId);
      await refreshRooms(activeRoomId);
      setError("");
    } catch (deleteError) {
      setError(String(deleteError?.message || "Falha ao apagar mensagens selecionadas."));
    } finally {
      setMemoryBusy(false);
    }
  }

  function handleToggleMusic() {
    if (disableLocalAudio) return;
    const audio = audioRef.current;
    if (!audio) return;
    if (musicStopped || audio.paused || !audio.src) {
      playRandomTrack({ force: !audio.src });
      setMusicStopped(false);
      return;
    }
    audio.pause();
    audio.currentTime = 0;
    setMusicStopped(true);
    setIsMusicPlaying(false);
  }

  function handleNextMusic() {
    if (disableLocalAudio) return;
    playRandomTrack({ force: true });
  }

  function handlePrevMusic() {
    if (disableLocalAudio) return;
    const history = musicHistoryRef.current;
    const previous = history.pop();
    setHasMusicHistory(history.length > 0);
    if (!previous) return;
    const audio = audioRef.current;
    if (!audio) return;
    try {
      audio.pause();
    } catch {}
    audio.src = previous;
    audio.currentTime = 0;
    audio
      .play()
      .then(() => {
        setMusicaAtual(previous);
        setMusicStopped(false);
        setIsMusicPlaying(true);
      })
      .catch(() => {
        setIsMusicPlaying(false);
      });
  }

  function renderTopMenuContents() {
    return (
      <div className="top-actions vh-menu-contents">
        <div className="top-status">
          <span className="top-status-text">
            {loadingRooms ? "Carregando salas..." : `${rooms.length || 0} sala(s) no Tribunal`}
          </span>
          <span className="music-status-badge">{musicStatusText}</span>
        </div>
        <button type="button" className="btn gothic-btn" onClick={() => onNavigate?.("/")}>
          Voltar ao modo normal
        </button>
        <button type="button" className="btn gothic-btn" onClick={handleOpenMemory} disabled={!activeRoomId || memoryBusy}>
          {memoryBusy ? "..." : "Memoria da sala"}
        </button>
        <button type="button" className="btn gothic-btn" onClick={handleGenerateInvite} disabled={!activeRoomId}>
          Compartilhar convite
        </button>
        <button
          type="button"
          className="btn gothic-btn"
          onClick={() => {
            if (judgementData || activeRoom?.latest_analysis) {
              setJudgementData(judgementData || activeRoom.latest_analysis);
              setJudgementOpen(true);
            }
          }}
          disabled={!activeRoom?.latest_analysis?.result && !judgementData?.analysis_result}
        >
          Ultimo veredito
        </button>
        {!disableLocalAudio ? (
          <>
            <button type="button" className="btn gothic-btn" onClick={handleToggleMusic}>
              {stopToggleLabel}
            </button>
            <button type="button" className="btn gothic-btn" onClick={handlePrevMusic} disabled={!hasMusicHistory}>
              Faixa anterior
            </button>
            <button type="button" className="btn gothic-btn" onClick={handleNextMusic}>
              Proxima musica
            </button>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="app gothic-text vh-chatrooms-app"
      style={{ "--vh-chatrooms-font-scale": String(fontScale) }}
    >
      {!disableLocalAudio ? <audio ref={audioRef} hidden preload="auto" /> : null}

      <div className="background">
        <div className="background-media-box">
          <img
            src="/fotos/vanhelsin.jpg"
            alt=""
            className="background-media background-photo"
            aria-hidden
          />
        </div>
      </div>

      <div className="vh-layout">
        {!sidebarCollapsed ? (
          <div className="vh-backdrop" onClick={() => setSidebarCollapsed(true)} aria-hidden="true" />
        ) : null}

        <button
          type="button"
          className={`vh-toggle ${sidebarCollapsed ? "is-collapsed" : "is-open"}`}
          onClick={() => setSidebarCollapsed((value) => !value)}
          aria-label={sidebarCollapsed ? "Abrir salas" : "Fechar salas"}
          title={sidebarCollapsed ? "Abrir salas" : "Fechar salas"}
        >
          <span className="vh-toggle-icon">{sidebarCollapsed ? "✛" : "✕"}</span>
        </button>

        <div className={`vh-sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
          <aside className="conversations-sidebar vh-chatrooms-sidebar-panel">
            <div className="vh-sidebar-header">
              <button
                type="button"
                className="vh-sidebar-close"
                onClick={() => setSidebarCollapsed(true)}
                aria-label="Fechar salas"
                title="Fechar salas"
              >
                <span aria-hidden="true" className="vh-close-icon">✕</span>
              </button>
              <div className="conversations-sidebar-header">
                <div className="conversations-sidebar-title">Tribunal do Cacador</div>
                <button
                  type="button"
                  className="conversations-new-btn btn gothic-btn"
                  onClick={() => refreshRooms(activeRoomId)}
                  disabled={loadingRooms}
                >
                  {loadingRooms ? "..." : "Atualizar"}
                </button>
              </div>
            </div>

            <form className="vh-chatrooms-create-form" onSubmit={handleCreateRoom}>
              <label className="vh-chatrooms-field">
                <span>Seu nome na sala</span>
                <input
                  className="vh-chatrooms-input"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="Como voce aparece nas salas"
                />
              </label>
              <label className="vh-chatrooms-field">
                <span>Nova sala</span>
                <input
                  className="vh-chatrooms-input"
                  value={createTitle}
                  onChange={(event) => setCreateTitle(event.target.value)}
                  placeholder="Titulo da sala"
                />
              </label>
              <button type="submit" className="btn gothic-btn" disabled={busy}>
                Criar sala
              </button>
              {error ? <div className="vh-chatrooms-sidebar-error">{error}</div> : null}
            </form>

            <div className="vh-chatrooms-accessibility-panel">
              <div className="vh-chatrooms-accessibility-title">Leitura</div>
              <label className="vh-chatrooms-field">
                <span>Tamanho do texto: {Math.round(fontScale * 100)}%</span>
                <input
                  type="range"
                  min="0.9"
                  max="1.6"
                  step="0.05"
                  value={fontScale}
                  onChange={(event) => setFontScale(normalizeFontScale(event.target.value))}
                />
              </label>
              <div className="vh-chatrooms-accessibility-actions">
                <button
                  type="button"
                  className="btn gothic-btn"
                  onClick={() => setFontScale((value) => normalizeFontScale(Number(value) - 0.1))}
                >
                  A-
                </button>
                <button type="button" className="btn gothic-btn" onClick={() => setFontScale(1)}>
                  Padrao
                </button>
                <button
                  type="button"
                  className="btn gothic-btn"
                  onClick={() => setFontScale((value) => normalizeFontScale(Number(value) + 0.1))}
                >
                  A+
                </button>
              </div>
            </div>

            <div className="conversations-sidebar-list">
              {(Array.isArray(rooms) ? rooms : []).map((room) => (
                <div
                  key={room.room_id}
                  className={`conversation-item ${room.room_id === selectedRoom?.room_id ? "active" : ""}`}
                >
                  <button
                    type="button"
                    className="conversation-open-btn"
                    onClick={() => {
                      setActiveRoomId(room.room_id);
                      setSidebarCollapsed(isCompactViewport());
                    }}
                  >
                    <div className="conversation-title">{room.title}</div>
                    <div className="conversation-preview">
                      {room.participant_count || 0} participante(s) | {room.message_count || 0} mensagem(ns)
                    </div>
                  </button>
                </div>
              ))}
              {!rooms.length && !loadingRooms ? (
                <div className="conversations-sidebar-empty">Nenhuma sala criada ainda.</div>
              ) : null}
            </div>
          </aside>
        </div>

        <main className={`vh-main ${sidebarCollapsed ? "" : "vh-has-sidebar"}`}>
          <div className="top-bar vh-topbar-desktop">
            <h1 className="text-glow top-title">
              ⚔️ Van Helsin IA — <span className="top-title-persona-wrap">Tribunal do Cacador</span>
            </h1>
            {renderTopMenuContents()}
          </div>

          {activeRoom ? (
            <ChatRoomView
              room={activeRoom}
              messages={messages}
              badges={badges}
              busy={busy}
              error={error}
              currentUserId={currentUserId}
              onSend={handleSend}
              onAnalyze={handleAnalyze}
              onOpenInvite={() => {
                setInviteOpen(true);
                if (!inviteData) handleGenerateInvite();
              }}
              onOpenJudgement={() => {
                if (judgementData || activeRoom?.latest_analysis) {
                  setJudgementData(judgementData || activeRoom.latest_analysis);
                  setJudgementOpen(true);
                }
              }}
              onVoicePauseMusic={pauseMusicForVoice}
              onVoiceResumeMusic={resumeMusicAfterVoice}
            />
          ) : (
            <div className="chat-layout vh-chatrooms-chat-layout">
              <div className="chat vh-chatrooms-chat vh-chatrooms-placeholder">
                <div className="vh-ai-banner">Selecione uma sala ou crie a primeira.</div>
                <div className="vh-chatrooms-placeholder-copy">
                  Este espaco replica a estetica do modo normal, mas o fluxo e isolado: pessoas conversam entre si e chamam o Van apenas quando desejarem um parecer.
                </div>
                <div className="vh-chatrooms-empty-composer-copy">
                  Crie uma sala para liberar o campo de mensagem e iniciar a conversa.
                </div>
              </div>
            </div>
          )}

          {null}
        </main>
      </div>

      <ChatRoomInvite
        open={inviteOpen}
        roomTitle={activeRoom?.room?.title || selectedRoom?.title || ""}
        inviteData={inviteData}
        onClose={() => setInviteOpen(false)}
        onGenerate={handleGenerateInvite}
        onRevoke={handleRevokeInvites}
      />

      <ChatRoomMemoryPanel
        open={memoryOpen}
        roomTitle={activeRoom?.room?.title || selectedRoom?.title || ""}
        memory={memory}
        messages={messages}
        busy={memoryBusy}
        canClearConversation={Boolean(activeRoom?.room?.owner_user_id) && String(activeRoom?.room?.owner_user_id) === currentUserId}
        onClose={() => setMemoryOpen(false)}
        onRebuild={handleRebuildMemory}
        onClearConversation={handleClearConversation}
        onDeleteSelectedMessages={handleDeleteSelectedMessages}
      />

      <VanJudgementModal
        open={judgementOpen}
        analysis={judgementData}
        conflictMapText={judgementData?.conflict_map_text || ""}
        onClose={() => setJudgementOpen(false)}
      />
    </div>
  );
}
